import { Component, forwardRef, inject, input, signal } from '@angular/core';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import {
  BuildingContractorAssignmentDto,
  BuildingContractorAssignmentMergeDto,
  TenantFilterDto,
  TenantItemDto,
  TenantService
} from '@artemis-software/wr-api';
import {
  AssignmentType,
  UnpaginatedPickListBehaviour,
} from '@shared/form-controls/generic-pick-list/generic-pick-list.component';
import { firstValueFrom } from 'rxjs';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { watch } from '@/utils/signals/watch';
import FacilityElementEnum = BuildingContractorAssignmentMergeDto.FacilityElementEnum;

@Component({
  selector: 'wr-facility-element-contractor-pick-list',
  templateUrl: './facility-element-contractor-pick-list.component.html',
  styleUrls: ['facility-element-contractor-pick-list.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FacilityElementContractorPickListComponent),
      multi: true,
    },
  ],
})
export class FacilityElementContractorPickListComponent extends AbstractFormControl<BuildingContractorAssignmentDto[]> implements UnpaginatedPickListBehaviour<TenantItemDto> {

  readonly tenantService = inject(TenantService);

  externalTenantId = input.required<string | undefined>();
  readonly selectedFacilityElement = signal<TenantFilterDto.FacilityElementEnum | null>(null);
  readonly selectedContractors = signal<TenantItemDto[]>([]);

  isPaginated = false as const;

  constructor() {
    super();
    watch(this.selectedFacilityElement, (newFacilityElement) => {
      this.setSelectedContractors(newFacilityElement);
    });
    watch(this.selectedContractors, () => {
      this.assignContractors();
    });
  }

  setSelectedContractors(facilityElement: FacilityElementEnum | null) {
    const assignedContractors = facilityElement
      ? this.valueSignal()
        ?.filter(assignment => assignment.facilityElement === facilityElement)
        .map(assignment => assignment.contractor)
      : undefined;
    this.selectedContractors.set(assignedContractors ?? []);
  }

  assignContractors() {
    const facilityElement = this.selectedFacilityElement();
    if (!facilityElement) return;

    const selectedContractors = this.selectedContractors();
    const selectedIds = new Set(selectedContractors.map(contractor => contractor.id));
    const kept = (this.value ?? []).filter(assignment =>
      assignment.facilityElement !== facilityElement || selectedIds.has(assignment.contractor.id),
    );
    const assignedIds = new Set(
      kept.filter(assignment => assignment.facilityElement === facilityElement)
        .map(assignment => assignment.contractor.id),
    );
    const added: BuildingContractorAssignmentDto[] = [];
    for (const contractor of selectedContractors) {
      if (!assignedIds.has(contractor.id)) {
        assignedIds.add(contractor.id);
        added.push({ contractor, facilityElement } as BuildingContractorAssignmentDto);
      }
    }

    this.value = [...kept, ...added];
  }

  getAllItems(): Promise<TenantItemDto[]> {
    const externalTenantId = this.externalTenantId();
    const selectedFacilityElement = this.selectedFacilityElement();
    if(!selectedFacilityElement || !externalTenantId) {
      return Promise.resolve([]);
    }
    return firstValueFrom(this.tenantService.findAllContractorsByFacilityElementAndExternalTenantId(selectedFacilityElement, externalTenantId));
  }

  getAssignmentType(item: TenantItemDto): AssignmentType {
    const buildingContractorAssignments = this.valueSignal();
    const selectedFacilityElement = this.selectedFacilityElement();

    if(!selectedFacilityElement || !buildingContractorAssignments) return AssignmentType.UNASSIGNED;

    const isAssigned = buildingContractorAssignments.some(
      (buildingContractorAssignment : BuildingContractorAssignmentDto)  =>
        buildingContractorAssignment.contractor.id == item.id
        && buildingContractorAssignment.facilityElement == selectedFacilityElement);

    if(isAssigned) {
      return AssignmentType.ASSIGNED;
    }

    return AssignmentType.UNASSIGNED;
  }

  getDisplayName(item: TenantItemDto): string {
    return item.name;
  }
}
